import { ref } from "vue";
import store from "@/store";
import { useNotificationStore } from "@/store/pinia/notifications";
import {
  updateUnArchiveRole,
  updateUnArchiveResponsibility,
  unArchiveDepartment,
  unArchivePosition,
} from "@/api";
import { checkOrgObjectNameAvailability } from "@/api/orgObjectNameAPI";
import { unarchiveJobFamily } from "@/api/jobFamilyAPI";
import { unarchiveJobCategory } from "@/api/jobCategoryAPI";
import { isDuplicateNameError } from "@/composables/useOrgObjectNameValidation";
import { NAME_IN_USE } from "@components/constants/orgObjectMessages";

export const UNARCHIVE_OBJECT_LABELS = {
  role: "Role",
  responsibility: "Responsibility",
  department: "Department",
  job_family: "Job Family",
  job_category: "Job Category",
  position: "Position",
};

// `name` is only passed on the rename path; a plain unarchive keeps its original call shape.
const UNARCHIVE_REQUESTS = {
  role: (accountId, id, name) =>
    name
      ? updateUnArchiveRole(accountId, "role", id, name)
      : updateUnArchiveRole(accountId, "role", id),
  responsibility: (accountId, id, name) =>
    name
      ? updateUnArchiveResponsibility(accountId, "responsibility", id, name)
      : updateUnArchiveResponsibility(accountId, "responsibility", id),
  department: (accountId, id, name) =>
    name ? unArchiveDepartment(accountId, id, name) : unArchiveDepartment(accountId, id),
  job_family: (accountId, id, name) =>
    name ? unarchiveJobFamily(accountId, id, name) : unarchiveJobFamily(accountId, id),
  job_category: (accountId, id, name) =>
    name ? unarchiveJobCategory(accountId, id, name) : unarchiveJobCategory(accountId, id),
  position: (accountId, id) => unArchivePosition(accountId, "position", id),
};

function failureMessage(err, label) {
  const detail = err && err.response && err.response.data && err.response.data.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  return `Unable to unarchive this ${label.toLowerCase()}. Please try again.`;
}

/**
 * One unarchive flow for every org object (ER-11902 / MDP-608).
 *
 * `start(target)` asks the backend whether the archived object's name is still free:
 * free -> `openConfirm()` (the host's normal confirm), taken -> the "Name already in
 * use" dialog (`conflictOpen`). `confirm()` unarchives under the current name and, if
 * that loses a race (409 *_DUPLICATE_NAME), switches to the conflict dialog.
 * `submitRename(newName)` unarchives with `name` (atomic on the backend).
 * Every failure is surfaced through `errorMessage` / `nameError`, never only logged.
 *
 * Positions have no uniqueness rule and always get the normal confirm.
 * The availability pre-check and the rename path are behind `idea-MDP-608`.
 *
 * @param {"role"|"responsibility"|"department"|"job_family"|"job_category"|"position"} type
 * @param {{ getAccountId: () => string|number, openConfirm: () => void,
 *           closeConfirm: () => void, onUnarchived?: (result: object) => any,
 *           setLoading?: (value: boolean) => void }} options
 */
export function useUnarchiveOrgObject(
  type,
  { getAccountId, openConfirm, closeConfirm, onUnarchived, setLoading } = {}
) {
  const label = UNARCHIVE_OBJECT_LABELS[type] || "Object";
  const target = ref(null);
  const conflictOpen = ref(false);
  const busy = ref(false);
  const errorMessage = ref("");
  const nameError = ref("");

  function flagEnabled() {
    return Boolean(store && store.getters && store.getters["featureFlags/ideaMDP608"]);
  }

  function setBusy(value) {
    busy.value = value;
    if (setLoading) setLoading(value);
  }

  function clearErrors() {
    errorMessage.value = "";
    nameError.value = "";
  }

  function showConflict() {
    if (closeConfirm) closeConfirm();
    clearErrors();
    conflictOpen.value = true;
  }

  async function start(item) {
    target.value = item;
    clearErrors();
    if (type === "position" || !flagEnabled()) {
      openConfirm();
      return;
    }
    setBusy(true);
    try {
      const response = await checkOrgObjectNameAvailability({
        accountId: getAccountId(),
        type,
        name: item.name,
        excludeId: item.id,
        jobFamilyId: item.jobFamilyId,
      });
      const result = (response && response.data) || {};
      if (result.available === false && result.code) showConflict();
      else openConfirm();
    } catch (err) {
      // A failed pre-check is advisory only: the normal confirm still runs, and its own
      // request (or the 409 race path in `run`) reports the real outcome.
      if (isDuplicateNameError(err)) showConflict();
      else openConfirm();
    } finally {
      setBusy(false);
    }
  }

  async function run(newName) {
    const item = target.value;
    if (!item) return false;
    setBusy(true);
    try {
      let response;
      try {
        response = await UNARCHIVE_REQUESTS[type](getAccountId(), item.id, newName);
      } catch (err) {
        if (newName && isDuplicateNameError(err)) {
          nameError.value = NAME_IN_USE;
        } else if (!newName && isDuplicateNameError(err) && flagEnabled()) {
          showConflict();
        } else {
          errorMessage.value = failureMessage(err, label);
        }
        return false;
      }
      const shownName = newName || item.name;
      if (closeConfirm) closeConfirm();
      conflictOpen.value = false;
      clearErrors();
      target.value = null;
      useNotificationStore().success(`${label} "${shownName}" unarchived.`);
      if (onUnarchived) await onUnarchived({ id: item.id, name: shownName, response });
      return true;
    } finally {
      setBusy(false);
    }
  }

  function confirm() {
    clearErrors();
    return run(undefined);
  }

  function submitRename(newName) {
    clearErrors();
    return run(String(newName || "").trim());
  }

  function close() {
    if (busy.value) return;
    if (closeConfirm) closeConfirm();
    conflictOpen.value = false;
    target.value = null;
    clearErrors();
  }

  return {
    target,
    conflictOpen,
    busy,
    errorMessage,
    nameError,
    start,
    confirm,
    submitRename,
    close,
    clearErrors,
  };
}
